import { System } from '../../../engine/ecs/core/System';
import { PacmanComponent } from '../components/PacmanComponent';
import { GhostComponent, GhostMode } from '../components/GhostComponent';
import { CollectibleComponent } from '../components/CollectibleComponent';
import { TransformComponent } from '../../../engine/ecs/components/TransformComponent';
import { EventManager } from '../../../engine/events/EventManager';
import { Event } from '../../../engine/events/Event';
import { Entity } from '../../../engine/ecs/core/Entity';

/**
 * Game events
 */
export class GameOverEvent extends Event {
  constructor() {
    super('game_over');
  }
}

export class LevelCompleteEvent extends Event {
  constructor() {
    super('level_complete');
  }
}

// Create a simple event for when Pacman dies
export class PacmanDiedEvent extends Event {
  constructor() {
    super('pacman_died');
  }
}

/**
 * GameStateSystem - Handles game state logic, collisions, and win/lose conditions
 */
export class GameStateSystem extends System {
  private eventManager: EventManager;
  private debugCounter: number = 0;

  constructor() {
    super();
    this.registerRequiredComponent(PacmanComponent);
    this.registerRequiredComponent(TransformComponent);
    this.eventManager = EventManager.getInstance();
  }

  public update(_deltaTime: number): void {
    // Check for all possible game state changes
    this.checkPacmanGhostCollisions();
    this.checkLevelCompletion();
  }

  /**
   * Check collisions between Pacman and ghosts
   */
  private checkPacmanGhostCollisions(): void {
    const pacmanEntities = this.getEntities();
    const allEntities = this.entityManager.getAllEntities();

    // Debug: Log system status occasionally
    this.debugCounter++;
    if (this.debugCounter % 300 === 0) {
      // Every 5 seconds at 60fps
      console.log(
        `GameStateSystem: Processing ${pacmanEntities.length} Pacman entities, ${allEntities.length} total entities`
      );
    }

    for (const pacmanEntity of pacmanEntities) {
      const pacmanTransform = this.componentManager.getComponent(
        pacmanEntity,
        TransformComponent
      ) as TransformComponent;
      const pacman = this.componentManager.getComponent(
        pacmanEntity,
        PacmanComponent
      ) as PacmanComponent;

      if (!pacman.isAlive) {
        if (this.debugCounter % 300 === 0) {
          console.log('GameStateSystem: Skipping dead Pacman');
        }
        continue;
      }

      // Skip collision if Pacman is invincible
      if (pacman.invulnerabilityTimer > 0) {
        if (this.debugCounter % 300 === 0) {
          console.log(
            `GameStateSystem: Pacman is invincible (${pacman.invulnerabilityTimer.toFixed(
              1
            )}s remaining)`
          );
        }
        continue;
      }

      // Check collision with each ghost
      for (const entity of allEntities) {
        if (this.componentManager.hasComponent(entity, GhostComponent)) {
          const ghostTransform = this.componentManager.getComponent(
            entity,
            TransformComponent
          ) as TransformComponent;
          const ghost = this.componentManager.getComponent(
            entity,
            GhostComponent
          ) as GhostComponent;

          // Calculate distance for debugging
          const distance = this.calculateDistance(
            pacmanTransform,
            ghostTransform
          );

          // Debug: Log distances occasionally
          if (this.debugCounter % 300 === 0) {
            console.log(
              `GameStateSystem: Distance to ${
                ghost.ghostType
              }: ${distance.toFixed(1)}px`
            );
          }

          if (this.checkCollision(pacmanTransform, ghostTransform)) {
            console.log(
              `GameStateSystem: COLLISION DETECTED! Pacman and ${
                ghost.ghostType
              } at distance ${distance.toFixed(1)}px`
            );

            if (ghost.isEdible) {
              // Pacman eats ghost
              ghost.mode = GhostMode.Dead;
              ghost.isEdible = false;
              console.log(`GameStateSystem: Pacman ate ${ghost.ghostType}!`);
              // Emit ghost eaten event for scoring with position data
              const event = new Event('ghost_eaten');
              (event as any).position = {
                x: pacmanTransform.position.x,
                y: pacmanTransform.position.y,
              };
              this.eventManager.publish(event);
            } else if (ghost.mode !== GhostMode.Dead) {
              // Ghost kills Pacman - RE-ENABLED!
              console.log(
                `GameStateSystem: Ghost ${ghost.ghostType} killed Pacman!`
              );
              pacman.isAlive = false;
              console.log('GameStateSystem: Publishing PacmanDiedEvent...');
              // Emit pacman died event with position data
              const event = new PacmanDiedEvent();
              (event as any).position = {
                x: pacmanTransform.position.x,
                y: pacmanTransform.position.y,
              };
              this.eventManager.publish(event);
              console.log('GameStateSystem: PacmanDiedEvent published!');
            }
          }
        }
      }
    }
  }

  /**
   * Check if level is complete (all dots collected)
   */
  private checkLevelCompletion(): void {
    const allEntities = this.entityManager.getAllEntities();
    const remainingDots = allEntities.filter((entity: Entity) => {
      if (this.componentManager.hasComponent(entity, CollectibleComponent)) {
        const collectible = this.componentManager.getComponent(
          entity,
          CollectibleComponent
        ) as CollectibleComponent;
        return collectible && !collectible.isConsumed;
      }
      return false;
    });

    if (remainingDots.length === 0) {
      console.log('GameStateSystem: Level completed! All dots collected!');
      // Emit level completion event
      this.eventManager.publish(new Event('level_complete'));

      // In a full implementation, you would:
      // - Show level completion screen
      // - Load next level
      // - Give bonus points
      // For now, just log the victory
      console.log(
        'GameStateSystem: LEVEL COMPLETE! Game would advance to next level.'
      );
    }
  }

  /**
   * Calculate distance between two entities
   */
  private calculateDistance(
    transform1: TransformComponent,
    transform2: TransformComponent
  ): number {
    return Math.sqrt(
      Math.pow(transform1.position.x - transform2.position.x, 2) +
        Math.pow(transform1.position.y - transform2.position.y, 2)
    );
  }

  /**
   * Check collision between two entities using distance-based detection
   */
  private checkCollision(
    transform1: TransformComponent,
    transform2: TransformComponent
  ): boolean {
    const dx = transform1.position.x - transform2.position.x;
    const dy = transform1.position.y - transform2.position.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    // Use a smaller collision threshold - only trigger when entities are actually touching
    const collisionThreshold = 15; // Reduced from 35 to 15 pixels

    if (distance <= collisionThreshold) {
      console.log(
        `GameStateSystem: Collision detected! Distance: ${distance.toFixed(
          2
        )}px, Threshold: ${collisionThreshold}px`
      );
      return true;
    }

    return false;
  }
}
